import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { ApiService } from '../../core/api.service';
import { AuthService } from '../../core/auth.service';
import { FinancialBudgetHead, FinancialManagementOverview } from '../../core/models';
import { PaginationComponent } from '../../core/pagination.component';
import { LocalCurrencyPipe, LocalDatePipe, LocalNumberPipe, TranslatePipe } from '../../core/translate.pipe';

type FinancialTab = 'utilization' | 'budget-heads' | 'expenditure-heads' | 'payments' | 'emd-refunds';

@Component({
  selector: 'app-financial-management',
  standalone: true,
  imports: [CommonModule, FormsModule, LocalCurrencyPipe, LocalDatePipe, LocalNumberPipe, PaginationComponent, TranslatePipe],
  template: `
    <section class="page-header financial-page-header">
      <div>
        <h1>{{ 'Financial Management' | t }}</h1>
        <p>{{ 'Budget allocation, expenditure, vendor payment and EMD refund monitoring.' | t }}</p>
      </div>
      <div class="financial-filter-actions">
        <label>
          {{ 'Financial Year' | t }}
          <select [(ngModel)]="financialYear" (change)="load()">
            <option *ngFor="let year of financialYears" [value]="year">{{ year }}</option>
          </select>
        </label>
        <button type="button" (click)="load()" [disabled]="loading">{{ 'Refresh' | t }}</button>
      </div>
    </section>

    <p class="error" *ngIf="error">{{ error | t }}</p>
    <p class="success" *ngIf="success">{{ success | t }}</p>

    <section class="financial-kpis" *ngIf="data">
      <article><span>{{ 'Total Allotted Fund' | t }}</span><strong>{{ total('allocation_amount') | lcurrency:'INR':'1.2-2' }}</strong></article>
      <article><span>{{ 'Committed Amount' | t }}</span><strong>{{ total('committed_amount') | lcurrency:'INR':'1.2-2' }}</strong></article>
      <article><span>{{ 'Paid Expenditure' | t }}</span><strong>{{ total('paid_amount') | lcurrency:'INR':'1.2-2' }}</strong></article>
      <article><span>{{ 'Available Balance' | t }}</span><strong>{{ total('balance_amount') | lcurrency:'INR':'1.2-2' }}</strong></article>
    </section>

    <section class="financial-groups" *ngIf="data">
      <article *ngFor="let group of data.group_summary" [class.programme]="group['expenditure_group'] === 'Programme'">
        <div>
          <span>{{ group['expenditure_group'] | t }}</span>
          <strong>{{ (group['expenditure_group'] + ' Expenditure') | t }}</strong>
        </div>
        <dl>
          <div><dt>{{ 'Allotted' | t }}</dt><dd>{{ number(group['allocation_amount']) | lcurrency:'INR':'1.2-2' }}</dd></div>
          <div><dt>{{ 'Committed' | t }}</dt><dd>{{ number(group['committed_amount']) | lcurrency:'INR':'1.2-2' }}</dd></div>
          <div><dt>{{ 'Paid' | t }}</dt><dd>{{ number(group['paid_amount']) | lcurrency:'INR':'1.2-2' }}</dd></div>
          <div><dt>{{ 'Balance' | t }}</dt><dd>{{ number(group['balance_amount']) | lcurrency:'INR':'1.2-2' }}</dd></div>
        </dl>
      </article>
    </section>

    <nav class="financial-tabs" aria-label="Financial management sections">
      <button type="button" [class.active]="tab === 'utilization'" (click)="selectTab('utilization')">{{ 'Fund Utilization Report' | t }}</button>
      <button type="button" [class.active]="tab === 'budget-heads'" (click)="selectTab('budget-heads')">{{ 'Budget Head Master' | t }}</button>
      <button type="button" [class.active]="tab === 'expenditure-heads'" (click)="selectTab('expenditure-heads')">{{ 'Expenditure Head Master' | t }}</button>
      <button type="button" [class.active]="tab === 'payments'" (click)="selectTab('payments')">{{ 'Vendor Payments' | t }}</button>
      <button type="button" [class.active]="tab === 'emd-refunds'" (click)="selectTab('emd-refunds')">{{ 'EMD Refund Status' | t }}</button>
    </nav>

    <section class="panel financial-section" *ngIf="data && tab === 'utilization'">
      <div class="financial-section-heading">
        <div><h2>{{ 'Fund Utilization Report' | t }}</h2><p>{{ financialYear }} - {{ 'paid expenditure is treated as utilized fund' | t }}</p></div>
        <div class="financial-section-actions">
          <button type="button" class="download excel" (click)="download('xlsx')">{{ 'common.downloadExcel' | t }}</button>
          <button type="button" class="download pdf" (click)="download('pdf')">{{ 'common.downloadPdf' | t }}</button>
        </div>
      </div>
      <div class="table-scroll"><ng-container *ngTemplateOutlet="budgetTable; context: { manage: false }"></ng-container></div>
      <app-pagination [total]="currentTotal" [page]="page" [pageSize]="pageSize" [pageSizes]="pageSizes" (pageChange)="goToPage($event)" (pageSizeChange)="changePageSize($event)"></app-pagination>
    </section>

    <section class="panel financial-section" *ngIf="data && tab === 'budget-heads'">
      <div class="financial-section-heading">
        <div><h2>{{ 'Budget Head and Allotted Fund Master' | t }}</h2><p>{{ 'Maintain financial-year allocations under Administrative or Programme expenditure.' | t }}</p></div>
        <button type="button" *ngIf="canWrite" (click)="openBudgetForm()">{{ 'Add New Budget Head' | t }}</button>
      </div>
      <div class="table-scroll"><ng-container *ngTemplateOutlet="budgetTable; context: { manage: true }"></ng-container></div>
      <app-pagination [total]="currentTotal" [page]="page" [pageSize]="pageSize" [pageSizes]="pageSizes" (pageChange)="goToPage($event)" (pageSizeChange)="changePageSize($event)"></app-pagination>
    </section>

    <ng-template #budgetTable let-manage="manage">
      <table class="financial-table">
        <thead><tr>
          <th>{{ 'Type' | t }}</th><th>{{ 'Budget Head Code' | t }}</th><th>{{ 'Budget Head' | t }}</th>
          <th>{{ 'Allotted Fund' | t }}</th><th>{{ 'Committed' | t }}</th><th>{{ 'Paid / Utilized' | t }}</th>
          <th>{{ 'Balance' | t }}</th><th>{{ 'Utilization %' | t }}</th><th *ngIf="manage && canWrite">{{ 'common.actions' | t }}</th>
        </tr></thead>
        <tbody>
          <ng-container *ngFor="let group of visibleBudgetGroups">
            <tr class="financial-group-row"><td [attr.colspan]="manage && canWrite ? 9 : 8">{{ group | t }} {{ 'Expenditure' | t }}</td></tr>
            <tr *ngFor="let head of headsFor(group)">
              <td>{{ head.expenditure_group | t }}</td><td>{{ head.head_code }}</td><td>{{ head.head_name | t }}</td>
              <td class="numeric-cell">{{ head.allocation_amount | lcurrency:'INR':'1.2-2' }}</td>
              <td class="numeric-cell">{{ head.committed_amount | lcurrency:'INR':'1.2-2' }}</td>
              <td class="numeric-cell">{{ head.paid_amount | lcurrency:'INR':'1.2-2' }}</td>
              <td class="numeric-cell">{{ head.balance_amount | lcurrency:'INR':'1.2-2' }}</td>
              <td class="numeric-cell">{{ head.utilization_percent | ln:'1.2-2' }}%</td>
              <td *ngIf="manage && canWrite">
                <div class="master-actions">
                  <button type="button" class="secondary" (click)="editBudgetHead(head)">{{ 'common.edit' | t }}</button>
                  <button type="button" class="danger" (click)="removeBudgetHead(head)">{{ 'common.delete' | t }}</button>
                </div>
              </td>
            </tr>
            <tr *ngIf="!headsFor(group).length"><td [attr.colspan]="manage && canWrite ? 9 : 8" class="empty-cell">{{ 'No budget heads found.' | t }}</td></tr>
          </ng-container>
        </tbody>
      </table>
    </ng-template>

    <section class="panel financial-section" *ngIf="data && tab === 'expenditure-heads'">
      <div class="financial-section-heading">
        <div><h2>{{ 'Expenditure Head Master' | t }}</h2><p>{{ 'Create expenditure heads under an allotted budget head.' | t }}</p></div>
        <button type="button" *ngIf="canWrite" (click)="openExpenditureForm()" [disabled]="!data.budget_heads.length">{{ 'Add New Expenditure Head' | t }}</button>
      </div>
      <div class="table-scroll"><table class="financial-table">
        <thead><tr><th>{{ 'Type' | t }}</th><th>{{ 'Budget Head' | t }}</th><th>{{ 'Expenditure Head Code' | t }}</th><th>{{ 'Expenditure Head' | t }}</th><th>{{ 'Remarks' | t }}</th><th *ngIf="canWrite">{{ 'common.actions' | t }}</th></tr></thead>
        <tbody>
          <tr *ngFor="let head of pagedExpenditureHeads">
            <td>{{ head['expenditure_group'] | t }}</td><td>{{ head['budget_head_code'] }} - {{ head['budget_head_name'] }}</td><td>{{ head['head_code'] }}</td><td>{{ head['head_name'] }}</td><td>{{ head['remarks'] || '-' }}</td>
            <td *ngIf="canWrite">
              <div class="master-actions">
                <button type="button" class="secondary" (click)="editExpenditureHead(head)">{{ 'common.edit' | t }}</button>
                <button type="button" class="danger" (click)="removeExpenditureHead(number(head['id']))">{{ 'common.delete' | t }}</button>
              </div>
            </td>
          </tr>
          <tr *ngIf="!data.expenditure_heads.length"><td [attr.colspan]="canWrite ? 6 : 5" class="empty-cell">{{ 'No expenditure heads have been added.' | t }}</td></tr>
        </tbody>
      </table></div>
      <app-pagination [total]="currentTotal" [page]="page" [pageSize]="pageSize" [pageSizes]="pageSizes" (pageChange)="goToPage($event)" (pageSizeChange)="changePageSize($event)"></app-pagination>
    </section>

    <section class="panel financial-section" *ngIf="data && tab === 'payments'">
      <div class="financial-section-heading"><div><h2>{{ 'Vendor Payment Tracking' | t }}</h2><p>{{ 'Invoice, payment and outstanding status from the Payment Tracker.' | t }}</p></div></div>
      <div class="table-scroll"><table class="financial-table">
        <thead><tr><th>{{ 'Vendor' | t }}</th><th>{{ 'PO No' | t }}</th><th>{{ 'Budget Head' | t }}</th><th>{{ 'Invoice No' | t }}</th><th>{{ 'Invoice Date' | t }}</th><th>{{ 'Invoice Amount' | t }}</th><th>{{ 'Paid Amount' | t }}</th><th>{{ 'Outstanding' | t }}</th><th>{{ 'Status' | t }}</th></tr></thead>
        <tbody>
          <tr *ngFor="let payment of pagedVendorPayments"><td>{{ payment['vendor_name'] }}</td><td>{{ payment['po_no'] }}</td><td>{{ payment['budget_head'] || '-' }}</td><td>{{ payment['invoice_no'] }}</td><td>{{ payment['invoice_date'] | ldate }}</td><td class="numeric-cell">{{ number(payment['invoice_amount']) | lcurrency:'INR':'1.2-2' }}</td><td class="numeric-cell">{{ number(payment['paid_amount']) | lcurrency:'INR':'1.2-2' }}</td><td class="numeric-cell">{{ number(payment['outstanding_amount']) | lcurrency:'INR':'1.2-2' }}</td><td><span class="status-pill">{{ payment['payment_status'] | t }}</span></td></tr>
          <tr *ngIf="!data.vendor_payments.length"><td colspan="9" class="empty-cell">{{ 'No vendor payments found.' | t }}</td></tr>
        </tbody>
      </table></div>
      <app-pagination [total]="currentTotal" [page]="page" [pageSize]="pageSize" [pageSizes]="pageSizes" (pageChange)="goToPage($event)" (pageSizeChange)="changePageSize($event)"></app-pagination>
    </section>

    <section class="panel financial-section" *ngIf="data && tab === 'emd-refunds'">
      <div class="financial-section-heading"><div><h2>{{ 'Refund of EMD Status' | t }}</h2><p>{{ 'Refund and return status from the EMD Tracker.' | t }}</p></div></div>
      <div class="table-scroll"><table class="financial-table">
        <thead><tr><th>{{ 'EMD ID' | t }}</th><th>{{ 'Tender No' | t }}</th><th>{{ 'Bidder / Vendor' | t }}</th><th>{{ 'Amount' | t }}</th><th>{{ 'Expiry Date' | t }}</th><th>{{ 'Returned Date' | t }}</th><th>{{ 'Refund Status' | t }}</th><th>{{ 'Details' | t }}</th></tr></thead>
        <tbody>
          <tr *ngFor="let emd of pagedEmdRefunds"><td>{{ emd['emd_unique_id'] || '-' }}</td><td>{{ emd['tender_number'] || '-' }}</td><td>{{ emd['bidder_name'] || '-' }}</td><td class="numeric-cell">{{ number(emd['amount']) | lcurrency:'INR':'1.2-2' }}</td><td>{{ emd['expiry_date'] | ldate }}</td><td>{{ emd['returned_date'] | ldate }}</td><td><span class="status-pill">{{ emd['refund_status'] | t }}</span></td><td>{{ emd['return_details'] || '-' }}</td></tr>
          <tr *ngIf="!data.emd_refunds.length"><td colspan="8" class="empty-cell">{{ 'No EMD records found.' | t }}</td></tr>
        </tbody>
      </table></div>
      <app-pagination [total]="currentTotal" [page]="page" [pageSize]="pageSize" [pageSizes]="pageSizes" (pageChange)="goToPage($event)" (pageSizeChange)="changePageSize($event)"></app-pagination>
    </section>

    <div class="modal-backdrop" *ngIf="showBudgetForm" (click)="showBudgetForm = false">
      <section class="view-modal financial-modal" (click)="$event.stopPropagation()">
        <div class="view-modal-header"><div><h2>{{ (editingBudgetHeadId ? 'Edit Budget Head and Allotted Fund' : 'Add New Budget Head') | t }}</h2><p>{{ financialYear }}</p></div><button type="button" (click)="showBudgetForm = false">{{ 'Close' | t }}</button></div>
        <div class="financial-form-grid">
          <label>{{ 'Expenditure Type' | t }}<select [(ngModel)]="budgetForm.expenditure_group"><option *ngFor="let group of expenditureGroups" [value]="group">{{ group | t }}</option></select></label>
          <label>{{ 'Budget Head Code' | t }}<input [(ngModel)]="budgetForm.head_code" maxlength="40"></label>
          <label class="wide">{{ 'Budget Head Name' | t }}<input [(ngModel)]="budgetForm.head_name" maxlength="180"></label>
          <label>{{ 'Allotted Fund' | t }}<input type="number" min="0" step="0.01" [(ngModel)]="budgetForm.allocation_amount"></label>
          <label class="wide">{{ 'Remarks' | t }}<textarea rows="3" [(ngModel)]="budgetForm.remarks"></textarea></label>
        </div>
        <div class="modal-actions"><button type="button" class="secondary" (click)="showBudgetForm = false">{{ 'common.cancel' | t }}</button><button type="button" (click)="saveBudgetHead()" [disabled]="saving || !budgetForm.head_code.trim() || !budgetForm.head_name.trim()">{{ 'common.save' | t }}</button></div>
      </section>
    </div>

    <div class="modal-backdrop" *ngIf="showExpenditureForm" (click)="showExpenditureForm = false">
      <section class="view-modal financial-modal" (click)="$event.stopPropagation()">
        <div class="view-modal-header"><div><h2>{{ (editingExpenditureHeadId ? 'Edit Expenditure Head' : 'Add New Expenditure Head') | t }}</h2><p>{{ 'Link the expenditure head to a budget allocation.' | t }}</p></div><button type="button" (click)="showExpenditureForm = false">{{ 'Close' | t }}</button></div>
        <div class="financial-form-grid">
          <label class="wide">{{ 'Budget Head' | t }}<select [(ngModel)]="expenditureForm.budget_head_id"><option *ngFor="let head of data?.budget_heads" [ngValue]="head.id">{{ head.expenditure_group }} - {{ head.head_code }} - {{ head.head_name }}</option></select></label>
          <label>{{ 'Expenditure Head Code' | t }}<input [(ngModel)]="expenditureForm.head_code" maxlength="40"></label>
          <label>{{ 'Expenditure Head Name' | t }}<input [(ngModel)]="expenditureForm.head_name" maxlength="180"></label>
          <label class="wide">{{ 'Remarks' | t }}<textarea rows="3" [(ngModel)]="expenditureForm.remarks"></textarea></label>
        </div>
        <div class="modal-actions"><button type="button" class="secondary" (click)="showExpenditureForm = false">{{ 'common.cancel' | t }}</button><button type="button" (click)="saveExpenditureHead()" [disabled]="saving || !expenditureForm.budget_head_id || !expenditureForm.head_code.trim() || !expenditureForm.head_name.trim()">{{ 'common.save' | t }}</button></div>
      </section>
    </div>
  `
})
export class FinancialManagementComponent implements OnInit {
  private readonly api = inject(ApiService);
  private readonly auth = inject(AuthService);
  readonly expenditureGroups: Array<'Administrative' | 'Programme'> = ['Administrative', 'Programme'];
  readonly financialYears = Array.from({ length: 8 }, (_, index) => {
    const now = new Date();
    const currentStart = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    const start = currentStart - index;
    return `${start}-${String(start + 1).slice(-2)}`;
  });
  financialYear = this.financialYears[0];
  tab: FinancialTab = 'utilization';
  readonly pageSizes = [10, 25, 50, 100];
  page = 1;
  pageSize = 10;
  data?: FinancialManagementOverview;
  loading = false;
  saving = false;
  error = '';
  success = '';
  showBudgetForm = false;
  showExpenditureForm = false;
  editingBudgetHeadId: number | null = null;
  editingExpenditureHeadId: number | null = null;
  budgetForm = { expenditure_group: 'Administrative', head_code: '', head_name: '', allocation_amount: 0, remarks: '' };
  expenditureForm = { budget_head_id: 0, head_code: '', head_name: '', remarks: '' };

  get canWrite(): boolean {
    return ['admin', 'finance', 'procurement_officer'].includes(this.auth.currentUser?.role || '');
  }

  ngOnInit(): void { this.load(); }

  load(): void {
    this.loading = true;
    this.api.financialManagement(this.financialYear).subscribe({
      next: data => { this.data = data; this.page = Math.min(this.page, this.totalPages); this.loading = false; this.error = ''; },
      error: response => { this.error = response?.error?.detail || 'Unable to load financial data.'; this.loading = false; }
    });
  }

  total(key: string): number { return this.number(this.data?.totals?.[key]); }
  number(value: unknown): number { return Number(value || 0); }
  headsFor(group: string): FinancialBudgetHead[] { return this.pagedBudgetHeads.filter(head => head.expenditure_group === group); }

  get currentTotal(): number {
    if (this.tab === 'utilization' || this.tab === 'budget-heads') return this.data?.budget_heads.length || 0;
    if (this.tab === 'expenditure-heads') return this.data?.expenditure_heads.length || 0;
    if (this.tab === 'payments') return this.data?.vendor_payments.length || 0;
    return this.data?.emd_refunds.length || 0;
  }

  get totalPages(): number { return Math.max(1, Math.ceil(this.currentTotal / this.pageSize)); }
  get pagedBudgetHeads(): FinancialBudgetHead[] { return this.pageSlice(this.data?.budget_heads || []); }
  get visibleBudgetGroups(): Array<'Administrative' | 'Programme'> {
    if (!this.pagedBudgetHeads.length) return this.expenditureGroups;
    return this.expenditureGroups.filter(group => this.headsFor(group).length);
  }
  get pagedExpenditureHeads(): Record<string, any>[] { return this.pageSlice(this.data?.expenditure_heads || []); }
  get pagedVendorPayments(): Record<string, any>[] { return this.pageSlice(this.data?.vendor_payments || []); }
  get pagedEmdRefunds(): Record<string, any>[] { return this.pageSlice(this.data?.emd_refunds || []); }

  selectTab(tab: FinancialTab): void { this.tab = tab; this.page = 1; }
  changePageSize(pageSize: number): void { this.pageSize = pageSize; this.page = 1; }
  goToPage(page: number): void { this.page = Math.min(Math.max(1, page), this.totalPages); }

  private pageSlice<T>(items: T[]): T[] {
    const start = (this.page - 1) * this.pageSize;
    return items.slice(start, start + this.pageSize);
  }

  openBudgetForm(): void {
    this.editingBudgetHeadId = null;
    this.budgetForm = { expenditure_group: 'Administrative', head_code: '', head_name: '', allocation_amount: 0, remarks: '' };
    this.showBudgetForm = true;
  }

  editBudgetHead(head: FinancialBudgetHead): void {
    this.editingBudgetHeadId = head.id;
    this.budgetForm = {
      expenditure_group: head.expenditure_group,
      head_code: head.head_code,
      head_name: head.head_name,
      allocation_amount: Number(head.allocation_amount || 0),
      remarks: head.remarks || ''
    };
    this.showBudgetForm = true;
  }

  openExpenditureForm(): void {
    this.editingExpenditureHeadId = null;
    this.expenditureForm = { budget_head_id: this.data?.budget_heads[0]?.id || 0, head_code: '', head_name: '', remarks: '' };
    this.showExpenditureForm = true;
  }

  editExpenditureHead(head: Record<string, any>): void {
    this.editingExpenditureHeadId = Number(head['id']);
    this.expenditureForm = {
      budget_head_id: Number(head['budget_head_id']),
      head_code: String(head['head_code'] || ''),
      head_name: String(head['head_name'] || ''),
      remarks: String(head['remarks'] || '')
    };
    this.showExpenditureForm = true;
  }

  saveBudgetHead(): void {
    this.saving = true;
    const editing = this.editingBudgetHeadId !== null;
    const request = editing
      ? this.api.updateFinancialBudgetHead(this.editingBudgetHeadId!, this.budgetForm)
      : this.api.createFinancialBudgetHead({ financial_year: this.financialYear, ...this.budgetForm });
    request.subscribe({
      next: () => { this.saving = false; this.showBudgetForm = false; this.success = editing ? 'Budget head updated successfully.' : 'Budget head added successfully.'; this.editingBudgetHeadId = null; this.load(); },
      error: response => { this.saving = false; this.error = response?.error?.detail || 'Unable to save budget head.'; }
    });
  }

  saveExpenditureHead(): void {
    this.saving = true;
    const editing = this.editingExpenditureHeadId !== null;
    const request = editing
      ? this.api.updateExpenditureHead(this.editingExpenditureHeadId!, this.expenditureForm)
      : this.api.createExpenditureHead(this.expenditureForm);
    request.subscribe({
      next: () => { this.saving = false; this.showExpenditureForm = false; this.success = editing ? 'Expenditure head updated successfully.' : 'Expenditure head added successfully.'; this.editingExpenditureHeadId = null; this.load(); },
      error: response => { this.saving = false; this.error = response?.error?.detail || 'Unable to save expenditure head.'; }
    });
  }

  removeBudgetHead(head: FinancialBudgetHead): void {
    if (!confirm(`Deactivate budget head ${head.head_code} - ${head.head_name}?`)) return;
    this.api.deleteFinancialBudgetHead(head.id).subscribe({ next: () => this.load(), error: response => this.error = response?.error?.detail || 'Unable to remove budget head.' });
  }

  removeExpenditureHead(id: number): void {
    if (!confirm('Deactivate this expenditure head?')) return;
    this.api.deleteExpenditureHead(id).subscribe({ next: () => this.load(), error: response => this.error = response?.error?.detail || 'Unable to remove expenditure head.' });
  }

  download(format: 'xlsx' | 'pdf'): void {
    const startYear = Number(this.financialYear.slice(0, 4));
    const filter = { period: 'range', startDate: `${startYear}-04-01`, endDate: `${startYear + 1}-03-31` };
    this.api.exportReport('budget-utilization', format, filter).subscribe({
      next: response => {
        if (!response.body) return;
        const url = URL.createObjectURL(response.body);
        const link = document.createElement('a');
        link.href = url; link.download = `fund-utilization-${this.financialYear}.${format}`; link.click();
        URL.revokeObjectURL(url);
      },
      error: () => this.error = 'Unable to download fund utilization report.'
    });
  }
}
